import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import { logger } from '@/lib/logger'
import { getDataProvider } from '@/lib/dataProvider'

// TypeScript interfaces based on the database schema
export interface ScenePlaylistConfig {
  id: string
  scene_id: string
  playlist_node_id: string
  volume: number
  created_at: string
  updated_at: string
}

export interface CreateScenePlaylistConfigData {
  scene_id: string
  playlist_node_id: string
  volume?: number
}

export interface UpdateScenePlaylistConfigData {
  volume?: number
}

export const useScenePlaylistConfigsStore = defineStore('scenePlaylistConfigs', () => {
  // State
  const configs = ref<Map<string, ScenePlaylistConfig>>(new Map())
  const loadingStates = ref<Map<string, boolean>>(new Map())
  const error = ref<string | null>(null)
  const isLoading = ref(false)

  // Getters
  const getConfigsForScene = computed(() => (sceneId: string) => {
    return Array.from(configs.value.values())
      .filter(config => config.scene_id === sceneId)
  })

  const getConfigForSceneAndNode = computed(() => (sceneId: string, nodeId: string) => {
    return Array.from(configs.value.values())
      .find(config => config.scene_id === sceneId && config.playlist_node_id === nodeId)
  })

  const getConfigById = computed(() => (id: string) => {
    return configs.value.get(id)
  })

  const isConfigLoading = computed(() => (id: string) => {
    return loadingStates.value.get(id) || false
  })

  // Actions
  const fetchConfigsForScene = async (sceneId: string): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    isLoading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      const data = await provider.query<ScenePlaylistConfig>('scene_playlist_configs', {
        filters: [{ column: 'scene_id', operator: 'eq', value: sceneId }]
      })

      // Update store with configs for this scene
      data.forEach(config => {
        configs.value.set(config.id, config)
      })

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  const fetchAllConfigs = async (): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    isLoading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      const data = await provider.query<ScenePlaylistConfig>('scene_playlist_configs')

      // Update store
      configs.value.clear()
      data.forEach(config => {
        configs.value.set(config.id, config)
      })

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      error.value = errorMessage
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Link a playlist node to a scene (upsert)
   */
  const linkToScene = async (playlistNodeId: string, sceneId: string): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    try {
      const provider = getDataProvider()
      const result = await provider.upsert<ScenePlaylistConfig>('scene_playlist_configs', {
        scene_id: sceneId,
        playlist_node_id: playlistNodeId,
        volume: 1.0
      }, 'scene_id,playlist_node_id')

      if (!result.success) {
        return { success: false, error: result.error }
      }

      // Update store
      if (result.data) {
        configs.value.set(result.data.id, result.data)
      }

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Unlink a playlist node from a scene
   */
  const unlinkFromScene = async (playlistNodeId: string, sceneId: string): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    try {
      const provider = getDataProvider()
      
      // Find the config first
      const config = getConfigForSceneAndNode.value(sceneId, playlistNodeId)
      if (!config) {
        return { success: true } // Already unlinked
      }

      const result = await provider.delete('scene_playlist_configs', config.id)

      if (!result.success) {
        return { success: false, error: result.error }
      }

      // Remove from store
      configs.value.delete(config.id)

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    }
  }

  const deleteConfig = async (id: string): Promise<{ success: boolean; error?: string }> => {
    const authStore = useAuthStore()
    
    if (!authStore.user) {
      return { success: false, error: 'User not authenticated' }
    }

    loadingStates.value.set(id, true)

    try {
      const provider = getDataProvider()
      const result = await provider.delete('scene_playlist_configs', id)

      if (!result.success) {
        return { success: false, error: result.error }
      }

      // Update store
      configs.value.delete(id)
      loadingStates.value.delete(id)

      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      return { success: false, error: errorMessage }
    } finally {
      loadingStates.value.set(id, false)
    }
  }

  const clearConfigsForScene = (sceneId: string) => {
    const sceneConfigs = getConfigsForScene.value(sceneId)
    sceneConfigs.forEach(config => {
      configs.value.delete(config.id)
      loadingStates.value.delete(config.id)
    })
  }

  return {
    // State
    configs: computed(() => configs.value),
    error: computed(() => error.value),
    isLoading: computed(() => isLoading.value),

    // Getters
    getConfigsForScene,
    getConfigForSceneAndNode,
    getConfigById,
    isConfigLoading,

    // Actions
    fetchConfigsForScene,
    fetchAllConfigs,
    linkToScene,
    unlinkFromScene,
    deleteConfig,
    clearConfigsForScene
  }
})
